import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Button, Input, Alert, Card, FormStack } from "@marekl/adminer/components";
import { BrandLockup } from "../../../components/BrandLockup";
import { SupportContacts } from "../../../components/SupportContacts";
import { useRequestPasswordReset } from "../../../auth/passwordReset";
import { ApiError } from "../../../lib/api";
import styles from "./ForgotPasswordPage.module.css";

/**
 * „Zapomenuté heslo": ask for a reset link.
 *
 * The confirmation deliberately does NOT say whether the address has an
 * account. The server answers alike either way (so the route cannot be used
 * to enumerate members), and a page that said „poslali jsme vám mail" only
 * for real addresses would give away exactly what the server refuses to.
 * The wording therefore describes a condition, not a fact: „pokud k téhle
 * adrese účet patří".
 */
export function ForgotPasswordPage() {
  const { mutate, isPending, isSuccess, error } = useRequestPasswordReset();
  const [email, setEmail] = useState("");

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutate(email.trim());
  }

  function errorMessage(): string | undefined {
    if (!error) return undefined;
    if (error instanceof ApiError) {
      const body = error.body as { message?: string } | null;
      return body?.message ?? "Nepodařilo se odeslat žádost.";
    }
    return "Neočekávaná chyba. Zkuste to prosím znovu.";
  }

  return (
    <div className={styles.page}>
      <Card className={styles.card}>
        <Card.Content>
          <div className={styles.header}>
            <BrandLockup />
            <p className={styles.subtitle}>Zapomenuté heslo</p>
          </div>

          {isSuccess ? (
            <div className={styles.form}>
              <Alert variant="ok">
                Pokud k téhle adrese patří účet, poslali jsme na ni odkaz pro nastavení
                nového hesla. Platí dvě hodiny.
              </Alert>
              <p className={styles.hint}>
                E-mail nedorazil? Zkontrolujte složku s nevyžádanou poštou, nebo to
                zkuste znovu za chvíli.
              </p>
              <p className={styles.backLink}>
                <Link to="/login">Zpět na přihlášení</Link>
              </p>
            </div>
          ) : (
            <FormStack as="form" onSubmit={handleSubmit} noValidate className={styles.form}>
              <p className={styles.lead}>
                Zadejte adresu, kterou se přihlašujete. Pošleme na ni odkaz pro
                nastavení nového hesla.
              </p>

              {errorMessage() && <Alert variant="err">{errorMessage()}</Alert>}

              <Input
                id="email"
                label="E-mail"
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <Button
                type="submit"
                variant="primary"
                block
                loading={isPending}
                disabled={!email.trim()}
              >
                Poslat odkaz
              </Button>

              <p className={styles.backLink}>
                <Link to="/login">Zpět na přihlášení</Link>
              </p>
            </FormStack>
          )}
        </Card.Content>
      </Card>
      <SupportContacts />
    </div>
  );
}
